import { DocumentSnapshot, logger, Timestamp } from "../firestoreImports"
import { User, UserPriorityLevel } from "./user"
import { Car, EngineType } from "./car"
import { OverflowInfo, ReplacedReservation } from "./other"
import { z } from "zod"
import moment from "moment"

export enum ReservationStatus {
    Abused = "abused",
    Canceled = "canceled",
    Confirmed = "confirmed",
    Declined = "declined",
    Pending = "pending"
}

export const computeReservationStatus = (
    abused: boolean,
    canceled: boolean,
    confirmed?: boolean | null,
    fallback: ReservationStatus = ReservationStatus.Pending
): ReservationStatus => {
    if (abused) return ReservationStatus.Abused
    if (canceled) return ReservationStatus.Canceled
    if (confirmed === true) return ReservationStatus.Confirmed
    if (confirmed === false) return ReservationStatus.Declined
    return fallback
}

export const reservationBaseShape = {
    userId: z.string(),
    startTime: z.custom<Timestamp>(),
    endTime: z.custom<Timestamp>(),
    confirmed: z.boolean().nullish(),
    canceled: z.boolean().nullish().default(false),
    abused: z.boolean().nullish().default(false),
    dateCanceled: z.custom<Timestamp>().nullish(),
    entryDates: z.array(z.custom<Timestamp>()).nullish().default([]),
    exitDates: z.array(z.custom<Timestamp>()).nullish().default([]),
    parkingSpot: z.string().nullish(),
    parkingSpotId: z.string().nullish(),
    priority: z.enum(UserPriorityLevel).nullish().default(UserPriorityLevel.Basic),
    carId: z.string().nullish(),
    rideId: z.string().nullish(),
    timestampCreated: z.custom<Timestamp>().nullish(),
    gateReservationsIds: z.array(z.string()).nullish().default([]),
    status: z.preprocess(val => (typeof val === "string" ? val.toLowerCase() : val), z.enum(ReservationStatus).default(ReservationStatus.Pending)),
    date: z.custom<Timestamp>().nullish() // Legacy date, Garage app needs this to work (as of 22. 12. 2025)
} as const

const reservationSchema = z.object(reservationBaseShape).transform(data => ({
    ...data,
    status: computeReservationStatus(data.abused ?? false, data.canceled ?? false, data.confirmed, data.status)
}))

export interface IReservation {
    id: string
    userId: string
    startTime: Date
    endTime: Date
    confirmed?: boolean | null // This is an exception with nullable boolean, because null means "pending"
    canceled: boolean
    abused: boolean
    dateCanceled?: Date
    entryDates?: Date[]
    exitDates?: Date[]
    parkingSpot?: string
    parkingSpotId?: string
    priority?: UserPriorityLevel
    carId?: string
    rideId?: string
    timestampCreated?: Date
    gateReservationsIds?: string[]
    status: ReservationStatus
}

export interface Reservation extends IReservation {}
export class Reservation {
    // Helper & Computed property
    parkingUsage?: any
    user?: User
    // Transient: reservations canceled/trimmed by ensureSingleActiveReservation when this one was created.
    replacedReservations?: ReplacedReservation[]
    // Transient: set when this reservation was booked at an overflow location because the requested one was full.
    overflow?: OverflowInfo

    constructor(params: IReservation) {
        Object.assign(this, params)
        this.status = computeReservationStatus(this.abused, this.canceled, this.confirmed, this.status)
    }

    static fromData(doc: DocumentSnapshot): Reservation | null {
        const raw = doc.data()
        if (!raw) {
            logger.warn(`Cannot create Reservation ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = reservationSchema.parse(raw)
            return new Reservation({
                id: doc.id,
                userId: parsed.userId,
                startTime: parsed.startTime.toDate(),
                endTime: parsed.endTime.toDate(),
                confirmed: parsed.confirmed,
                canceled: parsed.canceled ?? false,
                abused: parsed.abused ?? false,
                dateCanceled: parsed.dateCanceled?.toDate() ?? undefined,
                entryDates: parsed.entryDates?.map((t: Timestamp) => t.toDate()) ?? [],
                exitDates: parsed.exitDates?.map((t: Timestamp) => t.toDate()) ?? [],
                parkingSpot: parsed.parkingSpot ?? undefined,
                parkingSpotId: parsed.parkingSpotId ?? undefined,
                priority: parsed.priority ?? UserPriorityLevel.Basic,
                carId: parsed.carId ?? undefined,
                rideId: parsed.rideId ?? undefined,
                timestampCreated: parsed.timestampCreated?.toDate() ?? undefined,
                gateReservationsIds: parsed.gateReservationsIds ?? undefined,
                status: parsed.status
            })
        } catch (error) {
            logger.error(`Cannot create Reservation ${doc.id} from snapshot:`, error)
            return null
        }
    }

    car = (carsArg?: Car[]): Car | undefined => {
        const cars = carsArg ? carsArg : this.user?.cars
        if (!cars) {
            return undefined
        }
        const car = cars.find(c => c.id === this.carId)
        if (car) {
            return car
        }
        return cars[0]
    }

    // If a reservation has carId, check for cars if the one is electric, otherwise true if any car is electric
    isElectric = (carsArg?: Car[]): boolean => {
        const cars = carsArg ? carsArg : this.user?.cars
        if (!cars) {
            return false
        }
        const car = cars.find(c => c.id === this.carId)
        if (car) {
            return car.isElectric
        }
        return cars.some(c => c.isElectric)
    }

    isNaturalGas = (): boolean => {
        const cars = this.user?.cars
        const car = cars?.find(c => c.id === this.carId) ?? cars?.[0]
        return car?.engineType === EngineType.NaturalGas
    }

    reservationStatus = () => {
        return computeReservationStatus(this.abused, this.canceled, this.confirmed, this.status)
    }

    displayStatus = (): string => {
        if (this.abused) return "Abused"
        if (this.canceled) return "Canceled"
        return this.status
    }

    asData(): Record<string, unknown> {
        return reservationSchema.parse({
            userId: this.userId,
            startTime: Timestamp.fromDate(this.startTime),
            endTime: Timestamp.fromDate(this.endTime),
            confirmed: this.confirmed ?? null,
            canceled: this.canceled ?? false,
            abused: this.abused ?? false,
            dateCanceled: this.dateCanceled ? Timestamp.fromDate(this.dateCanceled) : undefined,
            entryDates: this.entryDates?.map((d: Date) => Timestamp.fromDate(d)),
            exitDates: this.exitDates?.map((d: Date) => Timestamp.fromDate(d)),
            parkingSpot: this.parkingSpot,
            parkingSpotId: this.parkingSpotId,
            priority: this.priority ?? UserPriorityLevel.Basic,
            carId: this.carId,
            rideId: this.rideId,
            timestampCreated: this.timestampCreated ?? Timestamp.now(),
            gateReservationsIds: this.gateReservationsIds,
            status: this.status,
            // Legacy date, Garage app needs this to work (as of 22. 12. 2025)
            date: Timestamp.fromDate(moment.tz(this.startTime, "Europe/Prague").set({ hour: 10, minute: 0, second: 0, millisecond: 0 }).toDate())
        })
    }
}
